<template>
  <div
    ref="pageWrapper"
    class="our-page-wrapper"
    :class="[pageWidthClass, pageClasses]"
    :data-cy="dataCy"
    :data-page-width="width"
  >
    <div class="our-page-additional-wrapper" :class="pageAdditionClasses">
      <div ref="page" class="our-page" :class="contentWidthClass">
        <div v-if="isShownHeader" class="our-page-header" :class="headerPaddingClass">
          <div class="our-page-header-left">
            <div class="our-page-header-left-wrapper">
              <!-- @slot Use it to add something before left side of the header. -->
              <slot name="header-left-before" />

              <!-- @slot Use it to customise left side of the header. -->
              <slot name="header-left">
                <div class="header-left">
                  <ArrowLink
                    v-if="isShownArrowButton"
                    :back-route="backRoute"
                    :data-cy="`${dataCy}-back`"
                  />

                  <div v-if="title" class="flex w-full items-center">
                    <OurHeader :text="title" :size="titleSize" color="black" />
                    <div v-if="loading" class="loader ml-3 mt-2"></div>
                  </div>

                  <div v-else-if="description" class="flex w-full items-center gap-3">
                    <div class="our-page-header-left-subtitle mt-0">
                      {{ description }}
                    </div>
                    <div v-if="loading" class="loader mt-2"></div>
                  </div>

                  <div v-if="description && title" class="our-page-header-left-subtitle">
                    {{ description }}
                  </div>
                </div>
              </slot>

              <!-- @slot Use it to add something after left side of the header. -->
              <slot name="header-left-after" />
            </div>
          </div>

          <div>
            <slot name="header-center" />
          </div>

          <div class="our-page-header-right">
            <!-- @slot Use it to customise right side of the header. -->
            <slot name="header-right" />
          </div>
        </div>

        <div class="our-page-content">
          <!-- @slot Use it to add main content. -->
          <slot />
        </div>

        <div
          v-if="isShownFooterSlot"
          :class="[reverseFooterClass, footerPaddingClass]"
          class="our-page-footer"
        >
          <div class="our-page-footer-left">
            <!-- @slot Use it to add something to the left side of the footer. -->
            <slot name="footer-left" />
          </div>

          <div class="our-page-footer-right">
            <!-- @slot Use it to add something to the right side of the footer. -->
            <slot name="footer-right" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isDisabledFixedRounding && !isMobileDevice" class="our-page-fixed-block-wrapper">
      <div class="fixed-block" :class="fixedBlockClass" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getThemeClass, componentConfig } from "@/services/_bit/UiService";

import ArrowLink from "./components/ArrowLink";
import OurHeader from "@/components/_bit/TextHeader";

export default {
  name: "OurPage",

  components: {
    ArrowLink,
    OurHeader,
  },

  inject: {
    topMenuNarrowDetailsLayout: { default: () => () => false },
  },

  props: {
    /**
     * Sets page title.
     */
    title: {
      type: String,
      default: "",
    },

    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Set size.
     * @values xs, sm, md, lg, xl, 2xl
     */
    titleSize: {
      type: String,
      default: "lg",
    },

    /**
     * Sets page description.
     */
    description: {
      type: String,
      default: "",
    },

    /**
     * Sets Vue router route params for back arrow
     */
    backRoute: {
      type: Object,
      default: () => ({}),
    },

    /**
     * The width of the container page.
     * @values xs, sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl, wide
     */
    width: {
      type: String,
      default: "wide",
    },

    /**
     * Reverse left and right footer blocks (in mobile version only).
     */
    mobileFooterReverse: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets full width for wrapper block, and fixed with for content (from size prop). Content will be aligned center.
     */
    widePage: {
      type: Boolean,
      default: false,
    },

    /**
     * Active side padding for header.
     */
    headerPadding: {
      type: Boolean,
      default: undefined,
    },

    /**
     * Active side padding for footer.
     */
    footerPadding: {
      type: Boolean,
      default: undefined,
    },

    /**
     * Sets background color gray.
     */
    gray: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["activeWizardKey"]),

    isTopMenuNarrowDetailsLayout() {
      return this.topMenuNarrowDetailsLayout();
    },

    isDisabledFixedRounding() {
      return componentConfig.ourPage?.disabledFixedRounding;
    },

    isHeaderPadding() {
      if (this.headerPadding !== undefined) return this.headerPadding;

      return componentConfig.ourPage?.headerPadding;
    },

    isFooterPadding() {
      if (this.footerPadding !== undefined) return this.footerPadding;

      return componentConfig.ourPage?.footerPadding;
    },

    reverseFooterClass() {
      return {
        "reverse-footer": this.mobileFooterReverse && this.isMobileDevice,
      };
    },

    isShownFooterSlot() {
      return !!this.$slots["footer-left"] || !!this.$slots["footer-right"];
    },

    isShownHeader() {
      return (
        this.title ||
        this.description ||
        !!this.$slots["header-left"] ||
        !!this.$slots["header-right"]
      );
    },

    isShownArrowButton() {
      return !!Object.keys(this.backRoute).length;
    },

    pageClasses() {
      let themeClass = getThemeClass();
      const minHeight = this.isMobileDevice ? "min-height-mobile" : "min-height-desktop";
      const rounding =
        !this.isDisabledFixedRounding && !this.isMobileDevice ? "fixed-rounding" : "rounding";
      const backgroundColor = this.gray ? "bg-color-gray" : "";
      const activeWizard = this.activeWizardKey ? "!overflow-visible" : "";

      return [themeClass, minHeight, rounding, backgroundColor, activeWizard];
    },

    widthClass() {
      return {
        "our-page-size-xs": this.width === "xs", // 400px
        "our-page-size-sm": this.width === "sm", //   500px
        "our-page-size-md": this.width === "md", //   600px
        "our-page-size-lg": this.width === "lg", //   700px
        "our-page-size-xl": this.width === "xl", //   800px
        "our-page-size-2xl": this.width === "2xl", // 900px
        "our-page-size-3xl": this.width === "3xl", // 1000px
        "our-page-size-4xl": this.width === "4xl", // 1100px
        "our-page-size-5xl": this.width === "5xl", // 1200px
        "our-page-size-wide": this.width === "wide", // 100%
      };
    },

    pageWidthClass() {
      if (this.isTopMenuNarrowDetailsLayout) {
        return "our-page-size-wide";
      }

      return !this.widePage ? this.widthClass : "our-page-size-wide";
    },

    pageAdditionClasses() {
      const rounding =
        !this.isDisabledFixedRounding && !this.isMobileDevice ? "fixed-rounding" : "";
      const backgroundColor = this.gray ? "bg-color-gray" : "";

      return [rounding, backgroundColor];
    },

    contentWidthClass() {
      return this.widePage ? this.widthClass : "";
    },

    headerPaddingClass() {
      return this.isHeaderPadding ? "header-padding" : "";
    },

    footerPaddingClass() {
      return this.isFooterPadding ? "footer-padding" : "";
    },

    fixedBlockClass() {
      return this.gray ? "bg-color-gray" : "";
    },
  },

  mounted() {
    const bodyClasses = document.querySelector("body").classList;

    this.gray ? bodyClasses.add("bg-gray-50") : bodyClasses.remove("bg-gray-50");
  },
};
</script>

<style lang="postcss" scoped>
.loader {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #000;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.our-page {
  @apply mx-auto;
  @apply min-h-full;
  .our-link-wrapper {
    @apply w-full md:w-fit;
  }

  &-wrapper {
    @apply min-h-full overflow-auto;

    &.rounding {
      @apply p-4 md:px-8 md:py-6;
      @apply md:rounded-r-2xl;
      @apply bg-white bg-cover bg-fixed bg-center;

      &.bg-color-gray {
        @apply bg-gray-50;
      }
    }

    &.fixed-rounding {
      @apply relative;
      @apply pr-0 md:pr-4;
      @apply md:rounded-none;
      @apply bg-none;
    }
  }

  &-additional-wrapper {
    min-height: 100%;

    &.fixed-rounding {
      @apply p-4 md:py-6 md:pl-8 md:pr-4;
      @apply bg-white bg-cover bg-fixed bg-center;
      min-height: 100vh;
    }

    &.bg-color-gray {
      @apply bg-gray-50;
    }
  }

  &-header {
    @apply flex items-center justify-between gap-3 md:flex-row;
    @apply mb-4 md:mb-6;
    &-right {
      .our-row {
        @apply justify-between;
      }
    }

    &-left {
      &-wrapper {
        @apply flex items-center space-x-4;

        .header-left {
          @apply flex flex-col;
        }
      }

      &-subtitle {
        @apply mt-1.5 text-base font-normal text-gray-600;
      }
    }
  }

  &-footer {
    @apply mb-0 mt-14 justify-between pt-8 md:flex;
    @apply space-y-4 md:space-y-0;
    @apply border-t border-gray-200;

    &-right,
    &-left {
      @apply md:flex;
      @apply space-y-4 md:space-x-4 md:space-y-0;

      &:deep(.button) {
        @apply w-full md:w-auto;
      }

      &:deep(.dropdown) {
        @apply w-full md:w-auto;

        .dropdown-wrapper-list {
          @apply mb-2 md:mb-0;
        }

        .dropdown-block {
          @apply relative md:absolute;
        }

        .dropdown-list-right {
          @apply w-full text-center;
        }

        .dropdown-wrapper {
          @apply block w-full;

          .svg-icon {
            @apply right-auto;
          }
        }
      }
    }
  }

  &-fixed-block-wrapper {
    @apply absolute right-4;

    .fixed-block {
      @apply fixed;
      @apply top-0;
      @apply w-4;
      height: 100vh;
      @apply rounded-r-2xl bg-white;
      pointer-events: none;

      &.bg-color-gray {
        @apply !bg-gray-50;
      }
    }
  }
}

.header-padding,
.footer-padding {
  @apply px-4 md:px-6;
}

.reverse-footer {
  @apply flex flex-col-reverse space-y-reverse;

  .our-page-footer-right {
    @apply flex flex-col-reverse space-y-reverse;
  }
}

.our-page-size-xs {
  @apply md:w-100;
}

.our-page-size-sm {
  @apply md:w-125;
}

.our-page-size-md {
  @apply md:w-150;
}

.our-page-size-lg {
  @apply md:w-175;
}

.our-page-size-xl {
  @apply md:w-200;
}

.our-page-size-2xl {
  @apply md:w-225;
}

.our-page-size-3xl {
  @apply md:w-250;
}

.our-page-size-4xl {
  @apply md:w-275;
}

.our-page-size-5xl {
  @apply md:w-300;
}

.our-page-size-wide {
  @apply md:w-full;
}

.min-height {
  &-desktop {
    min-height: 100vh;
  }

  &-mobile {
    min-height: calc(100vh - 3.375rem);
  }
}
</style>
